import React from 'react';
import { Globe, Phone, MapPin, Clock, Briefcase, Star } from 'lucide-react';
import { faviconFor, hostOf } from './businessLayout';

const Fact = ({ icon: Icon, children, title, d }) => children ? (
  <span title={title || (typeof children === 'string' ? children : '')}
    className={`inline-flex items-center gap-1.5 text-[12.5px] max-w-[320px] ${d ? 'text-gray-300' : 'text-gray-600'}`}>
    <Icon size={13} className="text-teal-500 shrink-0" /><span className="truncate">{children}</span>
  </span>
) : null;

export const ProfileHero = ({ brand, name, score, scoreRing, isDarkMode: d, actions }) => {
  const id = brand.identity || {}, c = brand.contact || {}, colors = brand.colors || [], gbp = brand.gbp;
  const fav = faviconFor(c.website);
  return (
    <div className={`relative overflow-hidden rounded-3xl border mb-8 ${d ? 'bg-white/[0.02] border-white/[0.07]' : 'bg-white border-gray-200/80'}`} data-testid="bp-hero">
      <div className="h-1.5 flex" aria-hidden="true">
        {(colors.length ? colors : ['#2dd4bf', '#22d3ee']).map(col => <span key={col} className="flex-1" style={{ background: col }} />)}
      </div>
      <div className="p-6 md:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
        <div className="flex items-center gap-5 min-w-0 flex-1">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 border ${d ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
            {fav ? <img src={fav} alt="" className="w-9 h-9 rounded-lg" /> : <Globe size={22} className="text-teal-500" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 data-testid="brand-profile-heading" className={`font-display text-2xl md:text-[28px] font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>{id.business_name || name || 'Brand Profile'}</h2>
              {id.business_model && <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border border-teal-400/20 bg-teal-400/10 ${d ? 'text-teal-300' : 'text-teal-700'}`} data-testid="bp-model-chip">{id.business_model}</span>}
              {gbp?.rating && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-400/10 ${d ? 'text-amber-300' : 'text-amber-700'}`}><Star size={11} className="fill-current" />{gbp.rating} · {gbp.review_count} reviews</span>}
            </div>
            <p data-testid="brand-profile-tagline" className={`text-[14px] mt-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{id.tagline || 'Business identity & voice'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">{scoreRing}{actions}</div>
      </div>
      <div className={`px-6 md:px-8 py-4 border-t flex flex-wrap gap-x-6 gap-y-2 ${d ? 'border-white/[0.06]' : 'border-gray-100'}`} data-testid="bp-facts">
        <Fact icon={Briefcase} d={d}>{id.industry}</Fact>
        <Fact icon={MapPin} d={d} title={c.address}>{id.service_area || c.address}</Fact>
        <Fact icon={Phone} d={d}>{c.phone}</Fact>
        <Fact icon={Globe} d={d}>{hostOf(c.website)}</Fact>
        <Fact icon={Clock} d={d} title={c.hours}>{c.hours}</Fact>
      </div>
    </div>
  );
};

export const ProfileNav = ({ ids, labels, filled, isDarkMode: d }) => (
  <nav className="hidden xl:block sticky top-24 self-start w-48 shrink-0" data-testid="bp-nav">
    <p className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] mb-3 px-3 ${d ? 'text-gray-500' : 'text-gray-400'}`}>Sections</p>
    {ids.map(id => (
      <button key={id} type="button" onClick={() => document.getElementById(`bp-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-left transition-colors ${d ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'}`}
        data-testid={`bp-nav-${id}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${filled[id] ? 'bg-teal-400' : d ? 'bg-white/20' : 'bg-gray-300'}`} />{labels[id]}
      </button>
    ))}
  </nav>
);
